import { fieldNameEnum } from '../../../../enums/fieldNameEnum';
import {
    DATA_TYPE_MODEL, getFieldNameByValue,
    SEARCH_OPERATION_MODEL,
    SEARCH_OPERATION_MODEL as OPERATION_KEY_MODEL
} from "../../Model/Model";

export const NULL_OPERATIONS = [OPERATION_KEY_MODEL.NU, OPERATION_KEY_MODEL.NN];
export const RANGE_OPERATIONS = [OPERATION_KEY_MODEL.BT, OPERATION_KEY_MODEL.NB];
export const LIST_OPERATIONS = [OPERATION_KEY_MODEL.IN, OPERATION_KEY_MODEL.NI];
export const EXACT_OPERATIONS = [OPERATION_KEY_MODEL.EQ, OPERATION_KEY_MODEL.NE];
export const COMPARISON_OPERATIONS = [OPERATION_KEY_MODEL.GT, OPERATION_KEY_MODEL.GTE, OPERATION_KEY_MODEL.LT, OPERATION_KEY_MODEL.LTE];

// ─── UI item types ────────────────────────────────────────────────────────────
export const ITEM_TYPE_MODEL = {
    RULE: 'RULE',
    GROUP: 'GROUP',
};

// ─── Factories ────────────────────────────────────────────────────────────────

let _idCounter = 0;
/**
 * Generates a unique string ID for rules and groups in the search builder.
 * Combines timestamp and a monotonic counter to avoid collisions within the same ms.
 *
 * @returns {string} Unique ID in the format `sf_<timestamp>_<counter>`.
 */
export const generateSearchId = () => `sf_${Date.now()}_${++_idCounter}`;

/**
 * Creates a blank rule object with all fields in their default empty state.
 * Used as the starting point for both standalone rules and rules inside groups.
 *
 * @returns {Object} Empty rule with a new unique id.
 */
export const createEmptyRule = () => ({
    id: generateSearchId(),
    connector: SEARCH_OPERATION_MODEL.AND,
    field: '',
    fieldName: '',
    typeAttribute: '',
    modelName: '',
    sort: 'ASC',
    condition: '',
    operationKey: '',
    valueAttribute: '',
    dateAttribute: null,
    fromValue: null,
    toValue: null,
    inputString: true,
    inputNumber: false,
    inputTwoNumbers: false,
    inputDropdown: false,
    inputDate: false,
    inputTwoDates: false,
    inputTransferableList: false,
    availablesList: [],
    assignedList: [],
    conditions: [],
    values: [],
    emptyFields: false,
    isValidDate: true,
    isValidFrom: true,
    isValidTo: true,
});

/**
 * Creates a top-level rule item (type RULE) for the search builder items list.
 * Wraps createEmptyRule with the ITEM_TYPE_MODEL.RULE type discriminator.
 *
 * @returns {Object} Standalone rule item ready to be added to the items array.
 */
export const createStandaloneRule = () => ({
    type: ITEM_TYPE_MODEL.RULE,
    ...createEmptyRule(),
});

/**
 * Creates an empty group item (type GROUP) with one blank rule inside.
 * Used when the user clicks "Add group" in the search builder.
 *
 * @returns {Object} Group item with a new unique id and one empty child rule.
 */
export const createEmptyGroup = () => ({
    type: ITEM_TYPE_MODEL.GROUP,
    id: generateSearchId(),
    connector: SEARCH_OPERATION_MODEL.AND,
    rules: [createEmptyRule()],
});

// ─── Validation ──────────────────────────────────────────────────────────────

/**
 * Validates a single rule and returns a copy with emptyFields set accordingly.
 * A rule is invalid if it has no field, no condition, or no value when the
 * selected operation requires one.
 *
 * @param {Object} rule - Rule object from the search builder state.
 * @returns {Object} Rule with updated emptyFields flag.
 */
export const validateRule = (rule) => {
    if (!rule.field) return { ...rule, emptyFields: true };
    if (!rule.condition) return { ...rule, emptyFields: true };

    const needsValue = !NULL_OPERATIONS.includes(rule.operationKey);
    if (!needsValue) return { ...rule, emptyFields: false };

    const hasValue =
        (rule.inputDate && rule.dateAttribute !== null && rule.isValidDate) ||
        (rule.inputTwoDates && rule.fromValue !== null && rule.isValidFrom && rule.toValue !== null && rule.isValidTo) ||
        (rule.inputTwoNumbers && rule.fromValue !== null && rule.fromValue !== '' && rule.toValue !== null && rule.toValue !== '' && rule.isValidTo) ||
        (rule.inputTransferableList && rule.assignedList.length > 0) ||
        (!rule.inputDate && !rule.inputTwoDates && !rule.inputTwoNumbers && !rule.inputTransferableList &&
            rule.valueAttribute !== '' && rule.valueAttribute !== null && rule.valueAttribute !== undefined);

    return { ...rule, emptyFields: !hasValue };
};

/**
 * Validates all rules inside a group.
 *
 * @param {Array<Object>} rules - Array of rule objects belonging to a group.
 * @returns {{ validated: Array<Object>, allValid: boolean }}
 *   validated — rules with updated emptyFields flags;
 *   allValid  — false if any rule failed validation.
 */
export const validateGroupRules = (rules) => {
    let allValid = true;
    const validated = rules.map(rule => {
        const v = validateRule(rule);
        if (v.emptyFields) allValid = false;
        return v;
    });
    return { validated, allValid };
};

/**
 * Validates all items (standalone rules and groups) in the search builder.
 * Delegates to validateRule for RULE items and validateGroupRules for GROUP items.
 *
 * @param {Array<Object>} items - Top-level items array from the search builder state.
 * @returns {{ validated: Array<Object>, allValid: boolean }}
 *   validated — items with updated emptyFields flags on each rule;
 *   allValid  — false if any rule in any item failed validation.
 */
export const validateItems = (items) => {
    let allValid = true;
    const validated = items.map(item => {
        if (item.type === ITEM_TYPE_MODEL.RULE) {
            const v = validateRule(item);
            if (v.emptyFields) allValid = false;
            return v;
        }
        const { validated: validatedRules, allValid: groupValid } = validateGroupRules(item.rules);
        if (!groupValid) allValid = false;
        return { ...item, rules: validatedRules };
    });
    return { validated, allValid };
};

// ─── Rule update payloads ─────────────────────────────────────────────────────

/**
 * Builds the update payload when the user selects a new field in a rule row.
 * Resets all condition, value, and input-type fields to their default state.
 *
 * @param {number|string} fieldId - ID of the selected attribute.
 * @param {string} fieldName - Backend filter key for the attribute.
 * @param {string} typeAttribute - Data type of the attribute (see DATA_TYPE_MODEL).
 * @param {string} modelName - Model name associated with the field (empty string if none).
 * @returns {Object} Partial rule state to merge via updateRuleData.
 */
export const createFieldChangeUpdate = (fieldId, fieldName, typeAttribute, modelName) => ({
    field: fieldId,
    fieldName,
    typeAttribute,
    modelName,
    condition: '',
    operationKey: '',
    valueAttribute: '',
    dateAttribute: null,
    fromValue: null,
    toValue: null,
    inputString: true,
    inputNumber: false,
    inputTwoNumbers: false,
    inputDropdown: false,
    inputDate: false,
    inputTwoDates: false,
    inputTransferableList: false,
    availablesList: [],
    assignedList: [],
    conditions: [],
    values: [],
    emptyFields: false,
});

/**
 * Builds the update payload when the user selects a condition for a rule.
 *
 * @param {number|string} conditionId - ID of the selected condition.
 * @param {string} operationKey - Operation key used to compute input visibility flags.
 * @param {Object} flags - Input visibility flags returned by computeInputFlags.
 * @param {boolean} flags.inputString - Show plain text input.
 * @param {boolean} flags.inputNumber - Restrict text input to numbers.
 * @param {boolean} flags.inputTwoNumbers - Show two number inputs (range).
 * @param {boolean} flags.inputDropdown - Show single-value dropdown.
 * @param {boolean} flags.inputDate - Show single date picker.
 * @param {boolean} flags.inputTwoDates - Show from/to date pickers.
 * @param {boolean} flags.inputTransferableList - Show multi-select list.
 * @param {Array<Object>} flags.availablesList - Options not yet selected.
 * @param {Array<Object>} flags.assignedList - Options already selected.
 * @param {string} flags.valueAttribute - Pre-populated text/number value.
 * @param {Date|null} flags.dateAttribute - Pre-populated single date value.
 * @param {Date|null} flags.fromValue - Pre-populated range start date.
 * @param {Date|null} flags.toValue - Pre-populated range end date.
 * @returns {Object} Partial rule state to merge via updateRuleData.
 */
export const createConditionChangeUpdate = (conditionId, operationKey, flags) => ({
    condition: conditionId,
    operationKey,
    emptyFields: false,
    inputString: flags.inputString,
    inputNumber: flags.inputNumber,
    inputTwoNumbers: flags.inputTwoNumbers,
    inputDropdown: flags.inputDropdown,
    inputDate: flags.inputDate,
    inputTwoDates: flags.inputTwoDates,
    inputTransferableList: flags.inputTransferableList,
    availablesList: flags.availablesList,
    assignedList: flags.assignedList,
    valueAttribute: flags.valueAttribute,
    dateAttribute: flags.dateAttribute,
    fromValue: flags.fromValue,
    toValue: flags.toValue,
});

/**
 * Builds the update payload when the user types a text or number value in a rule.
 *
 * @param {string|number} value - The new input value.
 * @returns {Object} Partial rule state to merge via updateRuleData.
 */
export const createValueUpdate = (value) => ({
    valueAttribute: value,
    emptyFields: false,
});

/**
 * Builds the update payload when the user assigns or removes items
 * in a transferable list input.
 *
 * @param {Array<Object>} assignedList - Items currently selected.
 * @param {Array<Object>} availablesList - Items not yet selected.
 * @returns {Object} Partial rule state to merge via updateRuleData.
 */
export const createAssignedListUpdate = (assignedList, availablesList) => ({
    assignedList,
    availablesList,
    emptyFields: false,
});

// ─── Input flags computation ──────────────────────────────────────────────────

/**
 * Determines which input component to show for a rule based on its data type
 * and selected operation. Also pre-populates availablesList for dropdown/list inputs.
 *
 * @param {string} typeAttribute - Data type of the field (see DATA_TYPE_MODEL).
 * @param {string} operationKey - Selected operation key (see OPERATION_KEY_MODEL).
 * @param {Array|Object} values - Available option values returned by the conditions API.
 * @param {string} fieldName - Backend filter key, used to look up enum labels.
 * @param {Function} t - i18next translation function for enum label resolution.
 * @returns {Object} Input visibility flags and pre-populated list data:
 *   inputString, inputNumber, inputTwoNumbers, inputDropdown,
 *   inputDate, inputTwoDates, inputTransferableList,
 *   availablesList, assignedList, valueAttribute, dateAttribute, fromValue, toValue.
 */
export const computeInputFlags = (typeAttribute, operationKey, values, fieldName, t) => {
    const base = {
        inputString: false,
        inputNumber: false,
        inputTwoNumbers: false,
        inputDropdown: false,
        inputDate: false,
        inputTwoDates: false,
        inputTransferableList: false,
        availablesList: [],
        assignedList: [],
        valueAttribute: '',
        dateAttribute: null,
        fromValue: null,
        toValue: null,
    };

    if (!operationKey) return { ...base, inputString: true };

    const safeValues = values || [];

    if (typeAttribute === DATA_TYPE_MODEL.STRING) {
        if (EXACT_OPERATIONS.includes(operationKey)) return { ...base, inputDropdown: true };
        if (LIST_OPERATIONS.includes(operationKey)) {
            return {
                ...base,
                inputTransferableList: true,
                availablesList: Object.values(safeValues).map(v => ({ id: v, name: v })),
            };
        }
        return { ...base, inputString: true };
    }

    if (typeAttribute === DATA_TYPE_MODEL.LONG) {
        if (EXACT_OPERATIONS.includes(operationKey)) return { ...base, inputDropdown: true };
        if (getFieldNameByValue(fieldName)) {
            return {
                ...base,
                inputTransferableList: true,
                availablesList: Object.values(safeValues).map(id => ({
                    id: parseInt(id),
                    name: t(fieldNameEnum[fieldName][+id]).toUpperCase(),
                })),
            };
        }
        return {
            ...base,
            inputTransferableList: true,
            availablesList: Object.values(safeValues).map(v => ({ id: v, name: v })),
        };
    }

    if (typeAttribute === DATA_TYPE_MODEL.INTEGER) {
        if (LIST_OPERATIONS.includes(operationKey)) {
            return {
                ...base,
                inputTransferableList: true,
                availablesList: Object.values(safeValues).map(v => ({ id: v, name: v })),
            };
        }
        if (RANGE_OPERATIONS.includes(operationKey)) return { ...base, inputTwoNumbers: true };
        return { ...base, inputString: true, inputNumber: true };
    }

    if (typeAttribute === DATA_TYPE_MODEL.BIGDECIMAL) {
        if (RANGE_OPERATIONS.includes(operationKey)) return { ...base, inputTwoNumbers: true };
        return { ...base, inputString: true, inputNumber: true };
    }

    if (typeAttribute === DATA_TYPE_MODEL.DATE) {
        if (RANGE_OPERATIONS.includes(operationKey)) return { ...base, inputTwoDates: true };
        return { ...base, inputDate: true };
    }

    if (typeAttribute === DATA_TYPE_MODEL.BOOLEAN) return { ...base, inputDropdown: true };

    if (typeAttribute === DATA_TYPE_MODEL.UUID) {
        if (EXACT_OPERATIONS.includes(operationKey)) return { ...base, inputDropdown: true };
        if (LIST_OPERATIONS.includes(operationKey)) {
            return {
                ...base,
                inputTransferableList: true,
                availablesList: safeValues.map(v => ({ id: v.id, name: v.name })),
            };
        }
        return { ...base, inputString: true };
    }

    return { ...base, inputString: true };
};
